/**
 * SubscriptionContext
 * 
 * Centralized context for subscription and billing state management.
 * 
 * IMPROVEMENTS over Svelte version:
 * - Single source of truth for all subscription state (was scattered across stores)
 * - Built-in caching with configurable TTL
 * - Unified upgrade flow handling
 * - Dismissal state management built-in
 * - Feature access computed once and memoized
 */

import { 
  createContext, 
  useContext, 
  useState, 
  useEffect, 
  useCallback, 
  useMemo,
  useRef,
  type ReactNode 
} from 'react'
import { useAuth } from './AuthContext'
import { getAllProperties } from '@/lib/api/api'
import { getBillingStatus, getPortalLink } from '@/lib/api/billing'
import { track } from '@/lib/utils/track'
import { 
  STORAGE_KEYS, 
  DISMISSAL_DURATION_MINUTES,
  LIMIT_WARNING_THRESHOLD,
  calculateTrialDaysLeft,
  getTrialUrgencyLevel,
  AGENT_PLAN_FEATURES,
  isAgentPlanId,
} from '@/lib/constants/pricing'
import {
  isPaymentRecoveryStatus,
  isTerminalSubscriptionStatus,
  needsInvoicePortal,
  propertyGrantsMembershipAccess,
  resolveActiveTrial,
  resolveEntitlement,
  resolvePlanTier,
} from '@/lib/utils/subscriptionEntitlement'
import type {
  SubscriptionState,
  FeatureAccess,
  UpgradeReason,
  BillingStatus,
} from '@/lib/types/billing'

// ============================================================================
// Context Types
// ============================================================================

interface SubscriptionContextType extends SubscriptionState {
  // Actions
  refreshSubscription: () => Promise<void>
  openPricingModal: (opts?: { reason?: string }) => void
  closePricingModal: () => void
  openUpgradeModal: (featureName: string) => void
  closeUpgradeModal: () => void
  handleUpgrade: (reason?: UpgradeReason) => Promise<void>
  /** Paid-plan CTA: Stripe portal deep-linked to `/subscriptions/{sub}/update`. */
  openUpdatePortal: () => Promise<boolean>
  dismissTrialBanner: () => void
  activateTrial: () => Promise<boolean>
  
  // Modal state
  isPricingModalOpen: boolean
  pricingModalReason: string | null
  isUpgradeModalOpen: boolean
  upgradeFeatureName: string | null
  
  // Computed states
  shouldShowTrialBanner: boolean
  trialUrgencyLevel: 'normal' | 'high' | 'critical' | 'expired'
  
  // Helpers
  hasFeature: (feature: keyof FeatureAccess) => boolean
  canAccessRoute: (routeId: string) => boolean
}

// ============================================================================
// Default State
// ============================================================================

const defaultFeatures: FeatureAccess = {
  analytics: true,
  realTimeAnalytics: false,
  splitTesting: false,
  activeSplitTestLimit: 0,
  heatmaps: false,
  heatmapLimit: 0,
  funnels: false,
  funnelLimit: 0,
  aiCopilot: false,
  crossDomainTracking: false,
  teamAccess: false,
  teamSeats: 0,
  whiteLabel: false,
  apiAccess: false,
  slackSupport: false,
  revenueAttribution: false,
  revenueLimit: 0,
}

const defaultState: SubscriptionState = {
  planKind: null,
  isAgentPlan: false,
  currentPlan: 'free',
  planName: 'Free',
  isFreePlan: true,
  hasActiveTrial: false,
  trialDaysLeft: 0,
  trialEndsAt: null,
  usedTrial: false,
  isLocalTrial: false,
  stripeTrialActivated: false,
  hasActiveSubscription: false,
  stripeSubscriptionId: null,
  subscriptionStatus: null,
  isReadOnly: false,
  readOnlyReason: null,
  eventLimit: 0,
  eventsUsed: 0,
  eventUsagePercent: 0,
  isApproachingLimit: false,
  isOverLimit: false,
  trackingPaused: false,
  websiteLimit: 0,
  websitesUsed: 0,
  hasReachedWebsiteLimit: false,
  features: defaultFeatures,
  isLoading: true,
  error: null,
}

// ============================================================================
// Context
// ============================================================================

const SubscriptionContext = createContext<SubscriptionContextType | null>(null)

/** Focus and visibilitychange both fire on tab return — only refetch once. */
const BILLING_REFRESH_DEBOUNCE_MS = 1000

// ============================================================================
// Dismissal Helpers
// ============================================================================

function isDismissed(key: string): boolean {
  try {
    const dismissed = localStorage.getItem(key)
    if (!dismissed) return false
    
    const expiresAt = parseInt(dismissed, 10)
    if (Date.now() > expiresAt) {
      localStorage.removeItem(key)
      return false
    }
    
    return true
  } catch {
    return false
  }
}

function setDismissed(key: string, durationMinutes: number): void {
  try {
    const expiresAt = Date.now() + durationMinutes * 60 * 1000
    localStorage.setItem(key, expiresAt.toString())
  } catch {
    // Silent fail
  }
}

// ============================================================================
// Provider Component
// ============================================================================

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth()
  
  // Core state
  const [state, setState] = useState<SubscriptionState>(() => defaultState)
  
  // Modal state
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false)
  const [pricingModalReason, setPricingModalReason] = useState<string | null>(null)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const [upgradeFeatureName, setUpgradeFeatureName] = useState<string | null>(null)
 
  // Dismissal state
  const [isTrialBannerDismissed, setIsTrialBannerDismissed] = useState(() => 
    isDismissed(STORAGE_KEYS.TRIAL_DIALOG_DISMISSED)
  )

  // Last successful `/billing/status` response for the current token. Fallback
  // for failed refetches only — see fetchSubscriptionData. Keyed by token so a
  // logout/login can never serve another account's status.
  const lastBillingRef = useRef<{ token: string; data: BillingStatus } | null>(null)

  // ============================================================================
  // Data Fetching
  // ============================================================================
  
  const fetchSubscriptionData = useCallback(async () => {
    if (!token) {
      lastBillingRef.current = null
      setState(prev => ({ 
        ...prev, 
        ...defaultState, 
        isLoading: false 
      }))
      return
    }
    
    try {
      // Fetch both properties and billing status in parallel
      const [propertiesData, billingFetch] = await Promise.all([
        getAllProperties(token).catch((err) => {
          console.error('[SubscriptionContext] getAllProperties error:', err);
          return null;
        }),
        getBillingStatus(token).catch((err) => {
          console.error('[SubscriptionContext] getBillingStatus error:', err);
          return null;
        }),
      ])

      // A failed billing fetch falls back to the last snapshot this session
      // observed for this token, instead of degrading to `stripeStatus: null`.
      // Null status fails open by design (an outage must not lock out paying
      // customers), but `/property/all` keeps the paid plan name after a
      // cancel — so without this, one failed refetch would wash a terminal
      // status away and reopen every isReadOnly gate. In-memory only: the
      // localStorage billing cache was removed for serving stale *paid* state,
      // and this ref only ever preserves what the server already said.
      if (billingFetch) {
        lastBillingRef.current = { token, data: billingFetch }
      }
      const billingData =
        billingFetch ??
        (lastBillingRef.current?.token === token ? lastBillingRef.current.data : null)
      
      const subscription = propertiesData?.subscription || {}
      const currentPlanName = propertiesData?.currentPlan || subscription.currentPlan || 'Free'
      
      const planTier = resolvePlanTier(currentPlanName)
      
      // Trial status — local onboarding trial is separate from a Stripe
      // subscription still in its trial window (user may be on Business/Scale).
      const isLocalTrial = billingData?.localTrial?.active || subscription.localTrialActive || false
      const stripeTrialing = billingData?.subscription?.trialing || subscription.isTrial || false
      const planSource = billingData?.plan?.source || subscription.planSource || null
      const stripeStatus = billingData?.subscription?.status?.toLowerCase() ?? null
      const hasPaidStripePlan =
        planTier !== 'free' &&
        planSource === 'stripe_subscription' &&
        !isTerminalSubscriptionStatus(stripeStatus)
      const hasActiveTrial = resolveActiveTrial({
        isLocalTrial,
        stripeTrialing,
        hasPaidStripePlan,
        stripeStatus,
      })
      
      const trialEndDate = billingData?.subscription?.trialEndsAt ||
                          billingData?.localTrial?.endsAt ||
                          subscription.trialEnd ||
                          subscription.localTrialEndsAt
      // trialEnd can be a Unix timestamp in seconds (e.g. "1774303657") or an ISO string
      const trialEndsAt = trialEndDate
        ? (() => {
            const n = Number(trialEndDate)
            // If it parses as a number and looks like seconds (< year 3000 in ms), convert
            return !isNaN(n) && n < 32503680000
              ? new Date(n * 1000)
              : new Date(trialEndDate)
          })()
        : null
      const trialDaysLeft = calculateTrialDaysLeft(trialEndsAt)
      
      // Usage stats
      const eventsUsed = subscription.eventsUsed || 0
      const eventLimit = subscription.eventLimit || 0
      const eventUsagePercent = eventLimit > 0 ? (eventsUsed / eventLimit) * 100 : 0
      
      const websitesUsed = propertiesData?.properties?.length || 0
      const websiteLimit = subscription.websiteLimit || 0
      
      // Workspace membership access: if the user is a non-owner member of any
      // property whose owner has an active paid subscription, grant full feature
      // access — their team plan covers them.
      const hasMembershipAccess = (propertiesData?.properties || []).some(
        propertyGrantsMembershipAccess
      )

      // Entitlement follows the Stripe status, not the plan name: a canceled
      // account keeps its paid `currentPlan` label server-side, so gating on
      // the name alone leaves paid features open after a cancel (SUPENG-210).
      // A churned paid account resolves to read-only — history stays readable,
      // creation is paywalled via `isReadOnly`.
      const entitlement = resolveEntitlement({
        planName: currentPlanName,
        planSource,
        stripeStatus,
        hasActiveTrial,
        propertyFeatures: propertiesData?.properties?.[0]?.features || {},
        hasMembershipAccess,
        usedTrial: billingData?.usedTrial ?? propertiesData?.usedTrial ?? false,
      })
      // Free start / Agents have no Stripe subscription; the prepaid ledger is
      // the entitlement, so they are active with the agent feature set.
      const planKind = billingData?.planKind ?? null
      const isAgentPlan = isAgentPlanId(billingData?.plan?.id, planKind)
      const features = isAgentPlan ? AGENT_PLAN_FEATURES : entitlement.features
      const hasActiveSubscription = isAgentPlan || entitlement.hasActiveSubscription
      const isReadOnly = isAgentPlan ? false : entitlement.isReadOnly
      const readOnlyReason = isAgentPlan ? null : entitlement.readOnlyReason

      const newState: SubscriptionState = {
        planKind,
        isAgentPlan,
        currentPlan: entitlement.planTier,
        planName: currentPlanName,
        // A read-only account is not on the Free plan — it kept its old tier's
        // history. It just can't create, which `isReadOnly` handles.
        isFreePlan: entitlement.planTier === 'free' && !hasActiveTrial,
        
        hasActiveTrial,
        trialDaysLeft,
        trialEndsAt,
        usedTrial: billingData?.usedTrial ?? propertiesData?.usedTrial ?? false,
        isLocalTrial,
        stripeTrialActivated: stripeTrialing,
        
        hasActiveSubscription,
        stripeSubscriptionId:
          billingData?.subscription?.id ||
          billingData?.payment?.subscriptionId ||
          null,
        subscriptionStatus: billingData?.subscription?.status || null,
        isReadOnly,
        readOnlyReason,
        
        eventLimit,
        eventsUsed,
        eventUsagePercent,
        isApproachingLimit: eventUsagePercent >= LIMIT_WARNING_THRESHOLD * 100,
        isOverLimit: eventsUsed >= eventLimit && eventLimit > 0,
        trackingPaused: subscription.trackingPaused || false,
        
        websiteLimit,
        websitesUsed,
        // Adding a site is creation, so a read-only account has reached its
        // limit regardless of the count. Without this, the paid cap the account
        // kept — or Scale's unlimited `websiteLimit: 0` — leaves Add site live
        // on the hub and in Workspace settings, which read this flag rather
        // than `usePlanUsage().websites.canAdd`.
        hasReachedWebsiteLimit:
          isReadOnly || (websiteLimit > 0 && websitesUsed >= websiteLimit),
        
        features,
        isLoading: false,
        error: null,
      }
      
      setState(newState)
      
    } catch (error) {
      console.error('[SubscriptionContext] Error in fetchSubscriptionData:', error);
      // Fail open: a billing API error must not block the user. Grant full access
      // so the paywall never fires due to a server/network failure.
      setState(prev => ({
        ...prev,
        isLoading: false,
        isFreePlan: false,
        hasActiveSubscription: true,
        hasActiveTrial: false,
        subscriptionStatus: 'active',
        isReadOnly: false,
  readOnlyReason: null,
        features: {
          ...prev.features,
          analytics: true,
          splitTesting: true,
          heatmaps: true,
          funnels: true,
          aiCopilot: true,
          revenueAttribution: true,
          apiAccess: true,
        },
        error: error as Error,
      }))
    }
  }, [token])
  
  // Initial fetch
  useEffect(() => {
    fetchSubscriptionData()
  }, [fetchSubscriptionData])

  // Cancelling happens in Stripe's Customer Portal, which opens in another tab
  // or window. Without this the tab the user returns to keeps its pre-cancel
  // entitlement until a remount, so paid features stay open after a cancel.
  useEffect(() => {
    if (!token) return

    let lastRefreshAt = 0

    const refreshOnReturn = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastRefreshAt < BILLING_REFRESH_DEBOUNCE_MS) return
      lastRefreshAt = now
      fetchSubscriptionData()
    }

    window.addEventListener('focus', refreshOnReturn)
    document.addEventListener('visibilitychange', refreshOnReturn)

    return () => {
      window.removeEventListener('focus', refreshOnReturn)
      document.removeEventListener('visibilitychange', refreshOnReturn)
    }
  }, [token, fetchSubscriptionData])

  // ============================================================================
  // Computed Values
  // ============================================================================
  
  const trialUrgencyLevel = useMemo(() => {
    if (!state.hasActiveTrial) return 'normal'
    return getTrialUrgencyLevel(state.trialDaysLeft)
  }, [state.hasActiveTrial, state.trialDaysLeft])
  
  const shouldShowTrialBanner = useMemo(() => {
    // Don't show if dismissed
    if (isTrialBannerDismissed) return false

    // Show if trial is active
    if (state.hasActiveTrial) return true

    // Show if approaching limit
    if (state.isApproachingLimit) return true

    // Show if over limit
    if (state.isOverLimit) return true

    return false
  }, [state.hasActiveTrial, state.isApproachingLimit, state.isOverLimit, isTrialBannerDismissed])

  // ============================================================================
  // Actions
  // ============================================================================
  
  const refreshSubscription = useCallback(async () => {
    // No cache to clear anymore - just fetch fresh data
    setState(prev => ({ ...prev, isLoading: true }))
    await fetchSubscriptionData()
  }, [fetchSubscriptionData])
  
  const openStripePortal = useCallback(async (): Promise<boolean> => {
    if (!token) return false
    let newWindow: Window | null = null
    try {
      newWindow = window.open('', '_blank')
      const { url } = await getPortalLink(token)
      if (url) {
        if (newWindow) newWindow.location.href = url
        else window.location.href = url
        return true
      }
      newWindow?.close()
      return false
    } catch (error) {
      newWindow?.close()
      console.error('Failed to open billing portal:', error)
      return false
    }
  }, [token])

  const openPricingModal = useCallback((opts?: { reason?: string }) => {
    // Read-only Upgrade buttons (and every other plan-picker CTA) land here.
    // `unpaid` is read-only, so those buttons never call `handleUpgrade` —
    // send them to the invoice in the portal instead of a second checkout.
    if (needsInvoicePortal(state.subscriptionStatus, state.stripeSubscriptionId)) {
      void openStripePortal()
      return
    }
    setPricingModalReason(opts?.reason ?? null)
    setIsPricingModalOpen(true)
  }, [state.subscriptionStatus, state.stripeSubscriptionId, openStripePortal])

  const closePricingModal = useCallback(() => {
    setIsPricingModalOpen(false)
    setPricingModalReason(null)
  }, [])
  
  const openUpgradeModal = useCallback((featureName: string) => {
    track('upgrade_modal_shown', token, { trigger_reason: 'feature_gate', feature: featureName })
    setUpgradeFeatureName(featureName)
    setIsUpgradeModalOpen(true)
  }, [token])
  
  const closeUpgradeModal = useCallback(() => {
    setIsUpgradeModalOpen(false)
    setUpgradeFeatureName(null)
  }, [])
  
  const dismissTrialBanner = useCallback(() => {
    setDismissed(STORAGE_KEYS.TRIAL_DIALOG_DISMISSED, DISMISSAL_DURATION_MINUTES)
    setIsTrialBannerDismissed(true)
  }, [])
  
  const activateTrial = useCallback(async (): Promise<boolean> => {
    return false
  }, [])
  
  /**
   * Unified upgrade handler
   * 
   * IMPROVEMENT: This consolidates the upgrade logic that was spread across
   * multiple components in the Svelte version. It handles:
   * 1. Users who haven't added a website → redirect to add-website
   * 2. Users with a failed payment (`past_due` / `unpaid`) → open Stripe portal
   * 3. Users with a website but no trial → open pricing modal
   * 4. Users on free/local trial → open pricing modal
   * 5. Read-only (canceled) users → open pricing modal (needs a new checkout)
   * 6. Paid subscribers → open Stripe portal
   */
  const handleUpgrade = useCallback(async (reason?: UpgradeReason) => {
    if (!token) {
      window.location.href = '/sign-in'
      return
    }
    
    // Check if user has added a website
    const hasAddedWebsite = state.websitesUsed > 0
    
    if (!hasAddedWebsite) {
      // Redirect to add website
      window.location.href = '/add-website?new=true'
      return
    }
    
    // A failed payment needs the invoice in the Stripe portal, not a new
    // checkout. `unpaid` is read-only for entitlement, so this has to be
    // checked before the branches below or the "Update Payment" banner would
    // send a paying customer to the plan picker.
    const needsPaymentUpdate = needsInvoicePortal(
      state.subscriptionStatus,
      state.stripeSubscriptionId,
    )

    // Check if user should see pricing modal
    const shouldOpenPricing =
      !needsPaymentUpdate &&
      (
        state.isFreePlan ||
        state.isLocalTrial ||
        // Canceled / incomplete_expired can't be revived in the portal.
        // `unpaid` is read-only too, but `needsPaymentUpdate` already
        // sent it to the invoice portal.
        state.isReadOnly ||
        (!state.hasActiveSubscription && !state.stripeSubscriptionId)
      )
    
    if (shouldOpenPricing) {
      closeUpgradeModal()
      openPricingModal()
      track('upgrade_modal_shown', token, { trigger_reason: reason ?? 'manual_upgrade' })
      return
    }
    
    // Paid subscribers and payment recovery → Stripe portal
    await openStripePortal()
  }, [token, state.websitesUsed, state.isFreePlan, state.isLocalTrial, state.isReadOnly, state.hasActiveSubscription, state.stripeSubscriptionId, state.subscriptionStatus, closeUpgradeModal, openPricingModal, openStripePortal])

  const openUpdatePortal = useCallback(async (): Promise<boolean> => {
    if (!token) return false

    // A plan-change session only makes sense on a healthy subscription.
    // `past_due` still counts as active, and `unpaid` / `canceled` reach here
    // from the hub. Routed directly rather than via `handleUpgrade`, whose
    // zero-sites redirect would bury the invoice portal / plan picker.
    if (
      needsInvoicePortal(state.subscriptionStatus, state.stripeSubscriptionId) ||
      (isPaymentRecoveryStatus(state.subscriptionStatus) && !state.isReadOnly)
    ) {
      return openStripePortal()
    }
    if (state.isReadOnly || isTerminalSubscriptionStatus(state.subscriptionStatus)) {
      openPricingModal()
      return true
    }

    let newWindow: Window | null = null
    try {
      newWindow = window.open('', '_blank')
      const { url } = await getPortalLink(token, {
        flow: 'update',
        subscriptionId: state.stripeSubscriptionId,
        scope: { planTier: state.currentPlan },
      })
      if (url) {
        if (newWindow) newWindow.location.href = url
        else window.location.href = url
        return true
      }
      newWindow?.close()
      return false
    } catch {
      newWindow?.close()
      return false
    }
  }, [token, state.currentPlan, state.stripeSubscriptionId, state.isReadOnly, state.subscriptionStatus, openStripePortal, openPricingModal])

  // ============================================================================
  // Feature Access Helpers
  // ============================================================================
  
  const hasFeature = useCallback((feature: keyof FeatureAccess): boolean => {
    return !!state.features[feature]
  }, [state.features])
  
  /**
   * Route-based access control
   * Maps route IDs to feature flags
   */
  const canAccessRoute = useCallback((routeId: string): boolean => {
    // Always allow analytics
    if (routeId === 'analytics' || routeId === 'traffic') return true
    
    // Map routes to features
    const routeToFeature: Record<string, keyof FeatureAccess> = {
      'heatmaps': 'heatmaps',
      'heatmap': 'heatmaps',
      'funnels': 'funnels',
      'funnel-analyzer': 'funnels',
      'split-testing': 'splitTesting',
      'experiments': 'splitTesting',
    }
    
    const feature = routeToFeature[routeId]
    if (!feature) return true // Unknown routes are allowed
    
    return hasFeature(feature)
  }, [hasFeature])

  // ============================================================================
  // Context Value
  // ============================================================================
  
  const value: SubscriptionContextType = useMemo(() => ({
    ...state,
    
    // Actions
    refreshSubscription,
    openPricingModal,
    closePricingModal,
    openUpgradeModal,
    closeUpgradeModal,
    handleUpgrade,
    openUpdatePortal,
    dismissTrialBanner,
    activateTrial,
    
    // Modal state
    isPricingModalOpen,
    pricingModalReason,
    isUpgradeModalOpen,
    upgradeFeatureName,
    
    // Computed
    shouldShowTrialBanner,
    trialUrgencyLevel,
    
    // Helpers
    hasFeature,
    canAccessRoute,
  }), [
    state,
    refreshSubscription,
    openPricingModal,
    closePricingModal,
    openUpgradeModal,
    closeUpgradeModal,
    handleUpgrade,
    openUpdatePortal,
    dismissTrialBanner,
    activateTrial,
    isPricingModalOpen,
    pricingModalReason,
    isUpgradeModalOpen,
    upgradeFeatureName,
    shouldShowTrialBanner,
    trialUrgencyLevel,
    hasFeature,
    canAccessRoute,
  ])

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

// Default context value for when used outside provider
const defaultContextValue: SubscriptionContextType = {
  ...defaultState,
  refreshSubscription: async () => {},
  openPricingModal: () => {},
  closePricingModal: () => {},
  openUpgradeModal: () => {},
  closeUpgradeModal: () => {},
  handleUpgrade: async () => {},
  openUpdatePortal: async () => false,
  dismissTrialBanner: () => {},
  activateTrial: async () => false,
  isPricingModalOpen: false,
  pricingModalReason: null,
  isUpgradeModalOpen: false,
  upgradeFeatureName: null,
  shouldShowTrialBanner: false,
  trialUrgencyLevel: 'normal',
  hasFeature: () => false,
  canAccessRoute: () => true,
}

export function useSubscription(): SubscriptionContextType {
  const context = useContext(SubscriptionContext)
  // Return safe defaults when used outside provider instead of throwing
  if (!context) {
    return defaultContextValue
  }
  return context
}

/**
 * Minimal SubscriptionProvider for direct/standalone mode (editor injected into customer site).
 * Grants full feature access so the AI chat is not gated by subscription checks.
 */
export function DirectModeSubscriptionProvider({ children }: { children: ReactNode }) {
  const value: SubscriptionContextType = {
    ...defaultContextValue,
    hasFeature: () => true,
    canAccessRoute: () => true,
  }
  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

// ============================================================================
// Standalone Hook (for components outside provider)
// ============================================================================

/**
 * Lightweight hook for checking feature access without full context
 * Useful for components that only need to check access
 */
function useFeatureAccess(feature: keyof FeatureAccess): {
  hasAccess: boolean
  isLoading: boolean
} {
  const context = useContext(SubscriptionContext)
  
  if (!context) {
    // Outside provider - assume no access
    return { hasAccess: false, isLoading: true }
  }
  
  return {
    hasAccess: context.hasFeature(feature),
    isLoading: context.isLoading,
  }
}

